import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Headphones, Mail, Lock, UserCog, User, KeyRound } from 'lucide-react';

export default function AuthPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('agent');
  const { login } = useAuth();
  const navigate = useNavigate();

  const [agentLogin, setAgentLogin] = useState({ email: '', password: '' });
  const [customerLogin, setCustomerLogin] = useState({ email: '', password: '' });

  // Forgot password (agents/admins)
  const [resetOpen, setResetOpen] = useState(false);
  const [resetForm, setResetForm] = useState({ email_or_username: '', secret_code: '', new_password: '', confirm_password: '' });
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');
    if (resetForm.new_password.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }
    if (resetForm.new_password !== resetForm.confirm_password) {
      setResetError('Passwords do not match');
      return;
    }
    setResetLoading(true);
    try {
      await authAPI.resetPasswordWithCode({
        email_or_username: resetForm.email_or_username.trim(),
        secret_code: resetForm.secret_code,
        new_password: resetForm.new_password,
      });
      setResetSuccess('Password reset successful. You can now sign in with your new password.');
      setResetForm({ email_or_username: '', secret_code: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setResetError(getErrorMessage(err));
    } finally {
      setResetLoading(false);
    }
  };

  const getErrorMessage = (err) => {
    if (err.response?.data?.detail) {
      const detail = err.response.data.detail;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        return detail.map(d => d.msg || d.message || String(d)).join(', ');
      }
      if (typeof detail === 'object') {
        return detail.msg || detail.message || JSON.stringify(detail);
      }
      return String(detail);
    }
    return err.message || 'An error occurred';
  };

  const handleAgentLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const user = await login(agentLogin.email, agentLogin.password);
      if (user.role === 'customer') {
        setError('Please use Customer tab to login');
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const user = await login(customerLogin.email, customerLogin.password);
      if (user.role !== 'customer') {
        setError('Please use Agent tab to login');
        return;
      }
      navigate('/portal');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-slate-200/50 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <Card className="bg-white border-slate-200 shadow-xl" data-testid="auth-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_project-builder-158/artifacts/n1qmhcgm_Logo.png" 
                alt="MVP IT Logo" 
                className="h-14 w-auto"
              />
            </div>
            <CardDescription className="text-slate-500">Sign in to your account</CardDescription>
          </CardHeader>

          <CardContent>
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100">
                <TabsTrigger 
                  value="agent" 
                  className="data-[state=active]:bg-white data-[state=active]:text-cyan-600 data-[state=active]:shadow-sm"
                  data-testid="agent-tab"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Agent
                </TabsTrigger>
                <TabsTrigger 
                  value="customer" 
                  className="data-[state=active]:bg-white data-[state=active]:text-cyan-600 data-[state=active]:shadow-sm"
                  data-testid="customer-tab"
                >
                  <User className="w-4 h-4 mr-2" />
                  Customer
                </TabsTrigger>
              </TabsList>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-4 text-sm" data-testid="auth-error">
                  {error}
                </div>
              )}

              <TabsContent value="agent" className="mt-0">
                <form onSubmit={handleAgentLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="agent-email" className="text-slate-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="agent-email"
                        type="email"
                        placeholder="agent@company.com"
                        value={agentLogin.email}
                        onChange={(e) => setAgentLogin({ ...agentLogin, email: e.target.value })}
                        className="pl-10 bg-white border-slate-200 focus:border-cyan-500 focus:ring-cyan-500"
                        required
                        data-testid="agent-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="agent-password" className="text-slate-700">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="agent-password"
                        type="password"
                        placeholder="••••••••"
                        value={agentLogin.password}
                        onChange={(e) => setAgentLogin({ ...agentLogin, password: e.target.value })}
                        className="pl-10 bg-white border-slate-200 focus:border-cyan-500 focus:ring-cyan-500"
                        required
                        data-testid="agent-password-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-full bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg shadow-cyan-500/25" 
                    disabled={isLoading}
                    data-testid="agent-login-btn"
                  >
                    {isLoading ? 'Signing in...' : 'Sign In as Agent'}
                  </Button>
                </form>

                <div className="text-center mt-3">
                  <button
                    type="button"
                    onClick={() => { setResetOpen(true); setResetError(''); setResetSuccess(''); }}
                    className="text-sm text-cyan-600 hover:text-cyan-700 hover:underline inline-flex items-center gap-1"
                    data-testid="forgot-password-link"
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                    Forgot password?
                  </button>
                </div>

                <p className="text-center text-sm text-slate-500 mt-3">
                  Agent or Admin access only
                </p>
              </TabsContent>

              <TabsContent value="customer" className="mt-0">
                <form onSubmit={handleCustomerLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer-email" className="text-slate-700">Email / Username</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="customer-email"
                        type="text"
                        placeholder="your@email.com or username"
                        value={customerLogin.email}
                        onChange={(e) => setCustomerLogin({ ...customerLogin, email: e.target.value })}
                        className="pl-10 bg-white border-slate-200 focus:border-cyan-500 focus:ring-cyan-500"
                        required
                        data-testid="customer-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="customer-password" className="text-slate-700">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="customer-password"
                        type="password"
                        placeholder="••••••••"
                        value={customerLogin.password}
                        onChange={(e) => setCustomerLogin({ ...customerLogin, password: e.target.value })}
                        className="pl-10 bg-white border-slate-200 focus:border-cyan-500 focus:ring-cyan-500"
                        required
                        data-testid="customer-password-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-full bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg shadow-cyan-500/25" 
                    disabled={isLoading}
                    data-testid="customer-login-btn"
                  >
                    {isLoading ? 'Signing in...' : 'Sign In as Customer'}
                  </Button>
                </form>

                <p className="text-center text-sm text-slate-500 mt-6">
                  Access your support portal
                </p>
              </TabsContent>
            </Tabs>

            <div className="mt-6 pt-4 border-t border-slate-100 text-center">
              <p className="text-xs text-slate-400">
                Need an account? Contact your administrator.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={resetOpen} onOpenChange={(open) => { setResetOpen(open); if (!open) { setResetError(''); setResetSuccess(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-cyan-600" />
              Reset Agent Password
            </DialogTitle>
            <DialogDescription>
              Enter your email/username, the secret code provided by your administrator, and a new password.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleResetPassword} className="space-y-3">
            {resetError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm" data-testid="reset-error">
                {resetError}
              </div>
            )}
            {resetSuccess && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded-lg text-sm" data-testid="reset-success">
                {resetSuccess}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="reset-identifier">Email or Username</Label>
              <Input
                id="reset-identifier"
                type="text"
                placeholder="agent@company.com"
                value={resetForm.email_or_username}
                onChange={(e) => setResetForm({ ...resetForm, email_or_username: e.target.value })}
                required
                data-testid="reset-identifier-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reset-code">Secret Code</Label>
              <Input
                id="reset-code"
                type="password"
                placeholder="Provided by your administrator"
                value={resetForm.secret_code}
                onChange={(e) => setResetForm({ ...resetForm, secret_code: e.target.value })}
                required
                data-testid="reset-code-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reset-newpw">New Password</Label>
              <Input
                id="reset-newpw"
                type="password"
                placeholder="Min. 6 characters"
                value={resetForm.new_password}
                onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })}
                required
                minLength={6}
                data-testid="reset-newpw-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reset-confirmpw">Confirm New Password</Label>
              <Input
                id="reset-confirmpw"
                type="password"
                placeholder="Retype new password"
                value={resetForm.confirm_password}
                onChange={(e) => setResetForm({ ...resetForm, confirm_password: e.target.value })}
                required
                minLength={6}
                data-testid="reset-confirmpw-input"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setResetOpen(false)}
                data-testid="reset-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                disabled={resetLoading}
                data-testid="reset-submit-btn"
              >
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
